/* eslint-disable max-len */
import { PFieldset, componentsReady } from '@porsche-design-system/components-react';
import { Form, Row, Col } from 'antd';
import { useField } from 'formik';
import { isEqual, isNil } from 'lodash/fp';
import numeral from 'numeral';
import { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { type PhoneFieldProps } from '../../../components/fields/ci/PhoneField';
import { onNumericKeyPress } from '../../../utilities/form';
import { StyledTextFieldWrapper } from './shared';
import { getTextFieldState } from './utils';

const FIELDSET_TAG = 'p-fieldset';
const TEXT_FIELD_TAG = 'p-text-field-wrapper';

const InvisibleLabel = styled.span`
    visibility: hidden;
`;

// note: to make the style of legend and label consistent
const fieldsetMessageCss = css`
    legend {
        margin-top: 0 !important;
        margin-bottom: 4px !important;
        font:
            400 1rem / calc(6px + 2.125ex) 'Porsche Next',
            'Arial Narrow',
            Arial,
            'Heiti SC',
            SimHei,
            sans-serif !important;
    }

    #message {
        margin-top: 4px;
    }
`;

const useOverrideStyle = (wrapperRef: React.RefObject<HTMLDivElement>) => {
    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }

        const wrapper = wrapperRef.current;

        componentsReady(wrapper).then(() => {
            const fieldsetElement = wrapper.querySelector(FIELDSET_TAG);
            const fieldSetShadowRoot = fieldsetElement?.shadowRoot;

            if (!fieldsetElement || !fieldSetShadowRoot) {
                return;
            }

            const style = document.createElement('style');
            style.textContent = fieldsetMessageCss.toString();
            fieldSetShadowRoot?.appendChild(style);

            const textFieldElements = fieldsetElement.querySelectorAll(TEXT_FIELD_TAG);

            // note: porsche component render the padding based on the offset width of the prefix element.
            // when the prefix element is in collapse(hidden), the padding is not rendered correctly.
            // this is a workaround to add in the padding manually.
            const prefixElement = textFieldElements[0];
            const prefixInput = prefixElement?.querySelector('input');
            prefixInput?.setAttribute(
                'style',
                `--p-internal-text-field-input-padding-start: calc(36px - 2px) !important;`
            );
        });
    }, [wrapperRef]);
};

const PhoneAndPrefixField = ({ name, itemProps, required, status, label, placeholder, disabled }: PhoneFieldProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [prefixField, prefixMeta, { setValue: setPrefixValue }] = useField({ name: `${name}.prefix` });
    const [valueField, valueMeta, { setValue }] = useField({ name: `${name}.value` });

    const meta = useMemo(
        () => ({
            error: prefixMeta.error || valueMeta.error,
            touched: prefixMeta.touched || valueMeta.touched,
            initialValue: valueMeta.initialValue,
            value: valueMeta.value,
        }),
        [
            prefixMeta.error,
            prefixMeta.touched,
            valueMeta.error,
            valueMeta.initialValue,
            valueMeta.touched,
            valueMeta.value,
        ]
    );

    const onNumberChange = e => {
        setPrefixValue(numeral(e.target.value).value());
    };

    useOverrideStyle(wrapperRef);

    const hasError = !!meta?.error && (meta?.touched || !isEqual(meta?.initialValue, meta?.value));
    const errorMessage = Array.isArray(meta?.error) ? '' : meta?.error;

    return (
        <div ref={wrapperRef}>
            <Form.Item {...itemProps} required={required}>
                <PFieldset labelSize="small" required={required} state={hasError ? 'error' : getTextFieldState(status)}>
                    <span slot="label">{label}</span>
                    <Row gutter={8}>
                        <Col lg={9} span={6} xl={6}>
                            <StyledTextFieldWrapper
                                state={hasError ? 'error' : getTextFieldState(status)}
                                unit="+"
                                unitPosition="prefix"
                                hideLabel
                            >
                                <span slot="label">{label}</span>
                                <input
                                    disabled={disabled}
                                    name={`${name}.prefix`}
                                    onChange={onNumberChange}
                                    onKeyPress={onNumericKeyPress}
                                    required={required}
                                    type={isNil(prefixField.value) ? 'text' : 'number'}
                                    value={isNil(prefixField.value) ? '' : prefixField.value}
                                />
                            </StyledTextFieldWrapper>
                        </Col>

                        <Col lg={15} span={18} xl={18}>
                            <StyledTextFieldWrapper state={hasError ? 'error' : getTextFieldState(status)} hideLabel>
                                <InvisibleLabel slot="label">{label}</InvisibleLabel>
                                <input
                                    disabled={disabled}
                                    name={`${name}.value`}
                                    onBlur={valueField.onBlur}
                                    onChange={event => setValue(event.target.value.replace(/[\D-]/g, ''))}
                                    placeholder={placeholder}
                                    required={required}
                                    type="text"
                                    value={valueField.value || ''}
                                />
                            </StyledTextFieldWrapper>
                        </Col>
                    </Row>
                    {hasError && <span slot="message">{errorMessage}</span>}
                </PFieldset>
            </Form.Item>
        </div>
    );
};

export default PhoneAndPrefixField;
